import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatService } from '../services/api';

// Query keys
export const chatKeys = {
  all: ['chat'],
  rooms: () => [...chatKeys.all, 'rooms'],
  messages: (roomId) => [...chatKeys.all, 'messages', roomId],
  roomUsers: (roomId) => [...chatKeys.all, 'room-users', roomId],
};

// Hook for getting all private chat rooms
export const useChatRooms = () => {
  return useQuery({
    queryKey: chatKeys.rooms(),
    queryFn: async () => {
      const response = await chatService.getAllRooms();
      return response.data?.data?.rooms || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (increased since we have real-time updates)
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes (reduced from 30 seconds)
  });
};

// Hook for getting room messages with infinite scroll
export const useRoomMessages = (roomId, limit = 20) => {
  return useInfiniteQuery({
    queryKey: chatKeys.messages(roomId),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await chatService.getRoomMessages(roomId, pageParam, limit);
      const data = response.data?.data || {};
      return {
        messages: data.messages || [],
        pagination: data.pagination || {},
        roomInfo: data.roomInfo || {},
        room: data.room || {},
        nextPage: pageParam + 1,
        hasMore: data.pagination?.hasNextPage || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextPage : undefined,
    enabled: !!roomId,
    staleTime: 5 * 60 * 1000, // 5 minutes (increased since we have real-time updates)
    refetchInterval: false, // Disable polling since we have real-time updates
  });
};

// Hook for getting room users
export const useRoomUsers = (roomId) => {
  return useQuery({
    queryKey: chatKeys.roomUsers(roomId),
    queryFn: async () => {
      const response = await chatService.getRoomUsers(roomId);
      return response.data?.data || { users: [], roomInfo: {} };
    },
    enabled: !!roomId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for sending a message
export const useSendMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ roomId, content }) => chatService.sendMessage(roomId, content),
    onSuccess: (data, variables) => {
      // Invalidate messages for this room
      queryClient.invalidateQueries({ 
        queryKey: chatKeys.messages(variables.roomId) 
      });
      
      // Invalidate rooms to update last message
      queryClient.invalidateQueries({ 
        queryKey: chatKeys.rooms() 
      });
    },
  });
};

// Hook for clearing unread messages
export const useClearUnreadMessages = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (roomId) => chatService.clearUnreadMessages(roomId),
    onMutate: async (roomId) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: chatKeys.rooms() });

      // Snapshot the previous value
      const previousRooms = queryClient.getQueryData(chatKeys.rooms());

      // Optimistically update to remove unread count for this room
      queryClient.setQueryData(chatKeys.rooms(), (old = []) => {
        const updatedRooms = old.map(room => {
          if (room.roomId === roomId || room._id === roomId) {
            return { ...room, unreadCount: 0, unreadMessages: 0 };
          }
          return room;
        });
        
        // Calculate total unread count for optimistic localStorage update
        const totalUnread = updatedRooms.reduce((total, room) => {
          return total + (room.unreadCount || room.unreadMessages || 0);
        }, 0);
        
        // Optimistically update localStorage
        localStorage.setItem('unreadMessages', totalUnread.toString());
        
        // Dispatch optimistic update event
        window.dispatchEvent(new CustomEvent('unreadMessagesUpdate', {
          detail: {
            unreadMessages: totalUnread,
            timestamp: new Date()
          }
        }));
        
        return updatedRooms;
      });

      // Return a context object with the snapshotted value
      return { previousRooms };
    },
    onSuccess: (data, roomId) => {
      console.log("Clear unread messages success:", data);
      
      // Invalidate messages for this room
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(roomId) });
      
      // Invalidate rooms to update unread count (this will refetch with real data)
      queryClient.invalidateQueries({ queryKey: chatKeys.rooms() });
      
      // Dispatch custom event to update sidebar unread count
      window.dispatchEvent(new CustomEvent('messageRead', {
        detail: {
          roomId,
          timestamp: new Date()
        }
      }));
      
      // Also dispatch immediate unread count update if we have the data
      if (data?.data?.userUnreadMessages !== undefined) {
        // Update localStorage first
        localStorage.setItem('unreadMessages', data.data.userUnreadMessages.toString());
        
        // Then dispatch the event
        window.dispatchEvent(new CustomEvent('unreadMessagesUpdate', {
          detail: {
            unreadMessages: data.data.userUnreadMessages,
            timestamp: new Date()
          }
        }));
      }
    },
    onError: (error, roomId, context) => {
      console.error("Failed to clear unread messages:", error);
      
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousRooms) {
        queryClient.setQueryData(chatKeys.rooms(), context.previousRooms);
      }
      
      // Don't refetch on error to prevent potential infinite loops
      // Let the user manually refresh if needed
    },
    onSettled: (data, error) => {
      // Only refetch on success to ensure we have the latest data
      // Don't refetch on error to prevent potential server spam
      if (!error) {
        queryClient.invalidateQueries({ queryKey: chatKeys.rooms() });
      }
    }
  });
};

// Hook for creating a new chat room
export const useCreateRoom = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: chatService.createRoom,
    onSuccess: () => {
      // Invalidate rooms to show new room
      queryClient.invalidateQueries({ queryKey: chatKeys.rooms() });
    },
  });
};

// Hook for marking messages as read (legacy support)
export const useMarkAsRead = () => {
  return useClearUnreadMessages();
}; 